import { type FC, useEffect, useMemo, useState } from "react";

import {
  noBarData,
  noData,
  validLineData,
  totalActivity,
  dailyActivity,
  lineChartTitles,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { ChartWrapper, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { type ColumnOption } from "interfaces/columnOptions";
import { GenericPerformanceList } from "../../shared/GenericPerformanceList";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { type CommonAccountReportProps } from "../../types";
import { sharedAccountReportingLineProps, sharedAccountReportingHorizontalBarProps } from "../../shared";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type ConnectedProps, connect } from "react-redux";

import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { doLinksExport } from "features/Reporting/state/content/contentActions";
import { bindActionCreators } from "@reduxjs/toolkit";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import styles from "../../contentOverviewReports.module.scss";
import linksOverviewStyles from "./linksOverviewReport.module.scss";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import * as performanceUtils from "../../../../Library/Common/utils/performanceUtils";
import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import {
  useLinkLineChartQuery,
  useLinkEngagementQuery,
  useLinksOverviewQuery,
} from "../../../../Library/ExternalLinks/Performance/Queries/queries";
import { type LinkOverviewDetails } from "features/Reporting/types/content";
import { EntityType } from "features/Library/Flows/Designer/types";
import classNames from "classnames";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export type Props = CommonAccountReportProps & PropsFromRedux;

export const LinksOverviewReport: FC<Props> = ({
  filter,
  setSelectedContent,
  setHasExportData,
  setExportAction,
  linksExport,
  setIsReportEnabled,
  resetIsReportEnabled,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = useFeatureFlag(FeatureFlags.LinksReport);
  const formatDateTime = useReportingFormatWithTimeZone();

  const dateRange = useMemo(() => {
    return performanceUtils.createDateRange(filter.dateFrom, filter.dateTo);
  }, [filter]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    performanceUtils.lineChartMargins.left + performanceUtils.lineChartMargins.right,
  );

  const lineChartQuery = useLinkLineChartQuery(filter, chartPeriod);
  const engagementQuery = useLinkEngagementQuery(filter, reportEnabled);
  const detailsQuery = useLinksOverviewQuery(filter, reportEnabled);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: !!detailsQuery.data && detailsQuery.data.length > 0,
      tooltipContent: "No data available for export. There are no links with activity data.",
    });
  }, [detailsQuery.data, setHasExportData]);

  // Setting up correct export method
  useEffect(() => {
    setExportAction({
      method: linksExport,
      args: [filter],
      isExportEnabled: true,
    });
  }, [filter, linksExport, setExportAction]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const lineData = useMemo(() => {
    const xData = [lineChartQuery.data?.date, lineChartQuery.data?.date];
    if (chartState === dailyActivity) {
      return {
        xData,
        yData: [lineChartQuery.data?.pageVisits, lineChartQuery.data?.clicks],
      };
    }
    return {
      xData,
      yData: [lineChartQuery.data?.cumulativePageVisits, lineChartQuery.data?.cumulativeClicks],
    };
  }, [chartState, lineChartQuery.data]);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  const linksOverviewColumnOptions: ColumnOption<LinkOverviewDetails>[] = [
    {
      name: "Title",
      width: 4,
      render(item) {
        return (
          <Tooltip
            target={
              <button
                className={styles.linkButton}
                onClick={() => {
                  setSelectedContent({
                    id: item.LinkId,
                    type: EntityType.Link,
                    name: item.LinkTitle,
                  });
                }}
              >
                <TextTruncate lines={2} className={styles.wordBreak}>
                  {item.LinkTitle}
                </TextTruncate>
              </button>
            }
            content={item.LinkTitle}
            showAlways
          />
        );
      },
    },
    {
      name: "Impressions",
      width: 2,
      render: ({ TotalPageVisits }: LinkOverviewDetails) => TotalPageVisits.toLocaleString(),
    },
    {
      name: "Total Clicks",
      width: 2,
      render: ({ TotalClicks }: LinkOverviewDetails) => TotalClicks.toLocaleString(),
    },
    {
      name: "Click-Through Rate",
      width: 3,
      render: ({ ClickThroughRate }: LinkOverviewDetails) => `${Math.round(ClickThroughRate * 100).toLocaleString()}%`,
    },
    {
      name: "Last Activity",
      width: 4,
      render: ({ LastActivity }: LinkOverviewDetails) => formatDateTime(LastActivity),
    },
  ];

  const activityColorScale = [reportingColors.yellow, reportingColors.greenDark];
  const activityLegendLabels = ["Impressions", "Clicks"];

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={lineChartTitles}
          showLegend
          legendLabels={activityLegendLabels}
          colorRange={activityColorScale}
          onChange={setChartState}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([
              lineChartQuery.data.clicks,
              lineChartQuery.data.pageVisits,
              lineChartQuery.data.cumulativeClicks,
              lineChartQuery.data.cumulativePageVisits,
            ]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xFormatterFunc={(d) => performanceUtils.getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
                colorRange={activityColorScale}
                {...lineData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>

      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={["User Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.barData.map((d: any) => d.value)) ? (
              <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementQuery.data.barData} />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>

      <div className={classNames(styles.performanceCardSection, linksOverviewStyles.cards)}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>

      <div className={styles.table}>
        <RequestStatusRenderer state={detailsQuery.status}>
          {detailsQuery.isSuccess && detailsQuery.data ? (
            <GenericPerformanceList
              title="Links"
              rows={detailsQuery.data}
              columns={linksOverviewColumnOptions}
              filter={filter}
            />
          ) : null}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
  linksExport: bindActionCreators(doLinksExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(LinksOverviewReport);
export default ConnectedComponent;
